import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  JoonUIColor,
  Typography,
  FlexBox,
  SPACING,
  Slider,
  Checkbox,
} from "@joonapp/web-shared"
import { useEffect, useMemo, useState } from "react"

import { difficultyRewards } from "../../../../constants"
import { useFamilyQuery } from "../../../../networking/queries"
import { useQuestGroupSeries } from "../../useQuestGroups"
import { useAddEditQuestStore } from "../useAddEditQuestStore"

type UserAssignDifficultyCardProps = {
  userId: number
  userName: string
  onClick: () => void
  isChecked: boolean
}

const UserAssignDifficultyCard = ({
  userId,
  userName,
  onClick,
  isChecked,
}: UserAssignDifficultyCardProps) => {
  const [isHovering, setIsHovering] = useState(false)
  const {
    assignedUsers,
    coinRewardsMap,
    setCoinRewardsMap,
    selectedQuest,
    questDifficulty,
  } = useAddEditQuestStore()
  const { data: family } = useFamilyQuery()
  const { questGroupSeries } = useQuestGroupSeries(selectedQuest)

  const initialValue = questDifficulty ? difficultyRewards[questDifficulty] : 10
  const isEditing = !!selectedQuest

  const onChangeDifficultyCoins = (value: string) => {
    const valueNum = parseInt(value, 10)
    if (value === "") return setCoinRewardsMap(userId, 0)
    if (isNaN(valueNum)) return
    if (valueNum > 30) return setCoinRewardsMap(userId, 30)
    if (valueNum < 1) return setCoinRewardsMap(userId, 1)
    else setCoinRewardsMap(userId, valueNum)
  }

  const sliderOptions = [1, 2, 3, 4, 5, 6, 8, 10, 12, 14, 17, 20, 23, 26, 30]

  const doNotAllowReassign =
    isEditing && assignedUsers.length >= (family?.profiles?.length || 0)

  const sliderValue = useMemo(() => {
    const findClosestValue = (num: number, options: number[]) => {
      return options.reduce((prev, curr) =>
        Math.abs(curr - num) < Math.abs(prev - num) ? curr : prev
      )
    }

    return findClosestValue(
      coinRewardsMap[userId] ?? initialValue,
      sliderOptions
    )
  }, [coinRewardsMap, userId, initialValue]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCoinRewardsMap(
      userId,
      questGroupSeries.find((series) => series.user_id === userId)
        ?.redeemable_reward ?? initialValue
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FlexBox
      id={userId.toString()}
      justify="space-between"
      direction="row"
      align="center"
      wrap={false}
      style={{
        width: "100%",
        border: `1px solid ${JoonUIColor.border.default}`,
        borderRadius: SPACING.space2,
      }}
    >
      <button
        onClick={onClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: isEditing ? "space-between" : "flex-start",
          alignItems: "center",
          borderRight: `1px solid ${JoonUIColor.border.default}`,
          padding: SPACING.space2,
          paddingLeft: isEditing ? SPACING.space3 : SPACING.space2,
          gap: SPACING.space2,
          width: "39%",
          cursor: doNotAllowReassign ? "default" : "pointer",
          pointerEvents: doNotAllowReassign ? "none" : "auto",
          borderTopLeftRadius: SPACING.space2,
          borderBottomLeftRadius: SPACING.space2,
          backgroundColor:
            !doNotAllowReassign && isHovering
              ? JoonUIColor.background.xlightGray
              : "transparent",
          transition: "background-color 0.2s ease-in-out",
        }}
      >
        {!isEditing && (
          <Checkbox
            selected={isChecked}
            onChange={() => {}}
            name={userName}
            hideBorder
          />
        )}

        <FlexBox style={{ overflowX: "hidden", width: "fit-content" }}>
          <Typography
            variant="bodySmallBold"
            textAlign="left"
            style={{
              textOverflow: "ellipsis",
              width: "100%",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {userName}
          </Typography>
        </FlexBox>

        {isEditing && !doNotAllowReassign && (
          <FontAwesomeIcon
            icon={faChevronDown}
            color={JoonUIColor.icon.neutral}
          />
        )}
      </button>

      <FlexBox
        direction="row"
        align="center"
        justify="space-between"
        style={{
          padding: SPACING.space2,
          width: "68%",
          height: "42px",
          paddingRight: SPACING.space2,
          borderTopRightRadius: SPACING.space2,
          borderBottomRightRadius: SPACING.space2,
          background: JoonUIColor.background.xlightGray,
          opacity: isChecked ? 1 : 0.2,
          pointerEvents: isChecked ? "auto" : "none",
          zIndex: 1000,
        }}
        wrap={false}
      >
        <Slider
          value={sliderValue}
          options={sliderOptions}
          setValue={(value) => setCoinRewardsMap(userId, value)}
          variant="secondary"
        />
        <FlexBox
          direction="row"
          align="center"
          justify="flex-end"
          gap={SPACING.space1}
          wrap={false}
          style={{ width: "42px", userSelect: "none" }}
        >
          <input
            style={{
              fontSize: "0.875rem",
              lineHeight: "1.25rem",
              textAlign: "right",
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
            }}
            value={coinRewardsMap[userId] ?? ""}
            onChange={(e) => onChangeDifficultyCoins(e.target.value)}
            onBlur={() => {
              if (!coinRewardsMap[userId]) {
                setCoinRewardsMap(userId, 1)
              }
            }}
          />
          <img
            style={{ height: SPACING.space3 }}
            src="/images/icons/coin-icon.png"
            alt="coins"
          />
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}

export default UserAssignDifficultyCard
